import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaAngleLeft } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import {
  addQuestion,
  getAddQuestionStatus,
  setIdleStatus,
  getQuestionError,
} from "../../../redux/slice/questionSlice";
import Loading from "../../common/Loading";
import AlertBox from "../../common/AlertBox";
import { fetchTopics, getTopics } from "../../../redux/slice/topicSlice";

const levels = ["Easy", "Medium", "Hard"];

const AddQuestionForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const status = useSelector(getAddQuestionStatus);
  const error = useSelector(getQuestionError);

  const topics = useSelector(getTopics);

  const [question, setQuestion] = useState("");
  const [questionError, setQuestionError] = useState("");

  const [options, setOptions] = useState([]);
  const [option, setOption] = useState("");
  const [optionError, setOptionError] = useState("");

  const [correctOption, setCorrectOption] = useState("");
  const [correctOptionError, setCorrectOptionError] = useState("");

  const [topic, setTopic] = useState("");
  const [topicError, setTopicError] = useState("");

  const [marks, setMarks] = useState("");
  const [marksError, setMarksError] = useState("");

  const [level, setLevel] = useState("");
  const [levelError, setLevelError] = useState("");

  const [subject, setSubject] = useState("");
  const [subjectError, setSubjectError] = useState("");

  useEffect(() => {
    if (!topics) {
      dispatch(fetchTopics());
    }
  }, [dispatch, topics]);

  const handleAddOption = (e) => {
    if (option !== "") {
      if (options.length < 4) {
        const check = options.find((op) => op.option === option);
        if (check) {
          setOptionError("Duplicate option");
        } else {
          setOptions([...options, { option: option }]);
          setOptionError("");
        }
      } else {
        setOptionError("Only Four Options Allowed");
      }
    } else {
      setOptionError("Cannot be Empty");
    }
    setOption("");
  };

  const handleCorrectOption = (e) => {
    setCorrectOption(e.target.value);
    setCorrectOptionError("");
  };

  const handleKeyPressOption = (e) => {
    if (e.key === "Enter") {
      handleAddOption();
    }
  };

  const handleAdd = (e) => {
    e.preventDefault();
    let err = false;

    if (!topic) {
      setTopicError("Topic is required");
      err = true;
    }

    if (!subject) {
      setSubjectError("Subject is required");
      err = true;
    }

    if (marks === "") {
      setMarksError("Marks is required");
      err = true;
    }

    if (!level) {
      setLevelError("Level is required");
      err = true;
    }

    if (options.length !== 4) {
      setOptionError("Four options are required");
      err = true;
    } else if (correctOption === "") {
      setCorrectOptionError("Correct option is required");
      err = true;
    }

    if (question === "") {
      setQuestionError("Question is required");
      err = true;
    }

    if (!err) {
      const optionsOfAnswer = options.map((a) =>
        a.option === correctOption
          ? { ...a, isCorrect: true }
          : { ...a, isCorrect: false }
      );

      const data = {
        questions: question,
        optionsOfAnswer,
        subjectId: subject,
        topicId: topic,
        level,
        marks,
      };
      dispatch(addQuestion(data));

      setQuestion("");
      setOption("");
      setOptions([]);
      setCorrectOption("");
      setOptionError("");
    }
  };

  const handleCancel = () => {
    setQuestion("");
    setOption("");
    setOptions([]);
    setCorrectOption("");
    setOptionError("");
    setSubject("");
    setTopic("");
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <section className="flex flex-col w-full sm:gap-12 gap-6">
      {status === "loading" ? <Loading /> : null}
      {status === "success" ? (
        <AlertBox
          type="success"
          message="Question added successfully"
          toDispatch={setIdleStatus}
        />
      ) : null}
      {status === "error" ? (
        <AlertBox type="error" message={error} toDispatch={setIdleStatus} />
      ) : null}
      <div className="flex w-full justify-between items-center">
        <h1 className="text-[24px] font-semibold text-adminTextSecondary">
          Add a Question
        </h1>
      </div>
      <div className="bg-primary flex flex-col gap-10 w-full sm:p-7 p-3 rounded-[4px] border border-adminMainBorder">
        <button
          type="button"
          onClick={handleBack}
          className="w-14 flex flex-row gap-2 items-center"
        >
          <FaAngleLeft />
          <span>Back</span>
        </button>
        <form onSubmit={handleAdd} className="flex flex-col gap-4 sm:px-4 p-0">
          {/* Question container */}
          <div className="flex flex-col gap-1">
            <label
              htmlFor="questions"
              className="font-semibold tracking-wide text-textPrimary"
            >
              Question:
            </label>
            <textarea
              id="questions"
              name="questions"
              rows={4}
              placeholder="Eg: What is tallest mountain in the world?"
              value={question}
              onChange={(e) => {
                setQuestion(e.target.value);
                setQuestionError("");
              }}
              className="py-2 w-full text-sm border border-gray-500 px-3 rounded-[4px] focus:outline-none text-textSecondary"
            ></textarea>
            <span className="pl-4 text-[12px] italic text-red-500">
              {questionError}
            </span>
          </div>

          {/* Topic, subject and level container */}
          <div className="flex sm:flex-row flex-col gap-4">
            {/* Topic container */}
            <div className="flex flex-col gap-1 w-full">
              <label
                htmlFor="topic"
                className="font-semibold tracking-wide text-textPrimary"
              >
                Topic:
              </label>
              <select
                className="admin-input-box text-textSecondary"
                onChange={(e) => {
                  setTopic(e.target.value);
                  setTopicError("");
                  setSubject("");
                }}
              >
                <option value="" className="text-textSecondary">
                  Select a topic
                </option>
                {topics?.map((entry) => (
                  <option
                    key={entry._id}
                    value={entry._id}
                    className="text-textSecondary"
                  >
                    {entry.topicName}
                  </option>
                ))}
              </select>
              <span className="pl-4 text-[12px] italic text-red-500">
                {topicError}
              </span>
            </div>

            {/* Subject container */}
            <div className="flex flex-col gap-1 w-full">
              <label
                htmlFor="subject"
                className="font-semibold tracking-wide text-textPrimary"
              >
                Subject:
              </label>
              <select
                className="admin-input-box text-textSecondary"
                onChange={(e) => {
                  setSubject(e.target.value);
                  setSubjectError("");
                }}
              >
                <option value="" className="text-textSecondary">
                  Select a subject
                </option>
                {topics?.map((_topic) =>
                  _topic._id === topic
                    ? _topic.subjectDetails.map((entry) => (
                        <option
                          key={entry._id}
                          value={entry._id}
                          className="text-textSecondary"
                        >
                          {entry.subjectName}
                        </option>
                      ))
                    : null
                )}
              </select>
              <span className="pl-4 text-[12px] italic text-red-500">
                {subjectError}
              </span>
            </div>

            {/* Level container */}
            <div className="flex flex-col gap-1 w-full">
              <label
                htmlFor="level"
                className="font-semibold tracking-wide text-textPrimary"
              >
                Level:
              </label>
              <select
                id="level"
                name="level"
                className="admin-input-box text-textSecondary"
                value={level}
                onChange={(e) => {
                  setLevel(e.target.value);
                  setLevelError("");
                }}
              >
                <option value="" className="text-textSecondary">
                  Select a level
                </option>
                {levels.map((entry) => (
                  <option
                    key={entry}
                    value={entry}
                    className="text-textSecondary"
                  >
                    {entry}
                  </option>
                ))}
              </select>
              <span className="pl-4 text-[12px] italic text-red-500">
                {levelError}
              </span>
            </div>
          </div>

          {/* Marks Container */}
          <div className="flex flex-col gap-1">
            <span className="font-semibold tracking-wide text-textPrimary">
              Marks:
            </span>
            <div className="flex flex-row gap-12 sm:pl-4 pl-0 items-center h-[28px]">
              <div className="flex items-center">
                <input
                  type="radio"
                  id="marks1"
                  name="marks"
                  className="text-textSecondary"
                  value="1"
                  onChange={(e) => {
                    setMarks(e.target.value);
                    setMarksError("");
                  }}
                />
                <label
                  htmlFor="marks1"
                  className="pl-2 text-sm text-textSecondary"
                >
                  1 marks
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="radio"
                  id="marks2"
                  name="marks"
                  className="text-textSecondary"
                  value="2"
                  onChange={(e) => {
                    setMarks(e.target.value);
                    setMarksError("");
                  }}
                />
                <label
                  htmlFor="marks2"
                  className="pl-2 text-sm text-textSecondary"
                >
                  2 marks
                </label>
              </div>
            </div>
            <span className="pl-4 text-[12px] italic text-red-500">
              {marksError}
            </span>
          </div>

          {/* Options */}
          <div className="flex flex-col gap-1">
            <span className="font-semibold tracking-wide text-textPrimary">
              Options:
            </span>
            <div className="flex flex-row justify-start gap-4 w-full">
              <input
                type="text"
                id="op1"
                name="op1"
                className="admin-input-box text-textSecondary sm:w-[200px] w-[130px]"
                placeholder="Eg: Mount K2"
                value={option}
                onChange={(e) => setOption(e.target.value)}
                onKeyDown={handleKeyPressOption}
              />
              <button
                type="button"
                onClick={handleAddOption}
                className="border border-adminRightTextPrimary text-adminRightTextPrimary h-[36px] px-4 rounded-[4px]"
              >
                <FaPlus />
              </button>
            </div>
            <span className="pl-4 text-[12px] italic text-red-500">
              {optionError}
            </span>

            {/* SHow all options in a radio style */}
            <div className="sm:pl-14 pl-4">
              {options?.map((op, index) => (
                <div key={index} className="flex items-center pb-1">
                  <input
                    type="radio"
                    id={op.option}
                    name="options"
                    className="text-textSecondary"
                    value={op.option}
                    onChange={handleCorrectOption}
                  />
                  <label
                    htmlFor={op.option}
                    className="pl-2 text-textSecondary"
                  >
                    {op.option}
                  </label>
                </div>
              ))}
            </div>
            <span className="pl-4 text-[12px] italic text-red-500">
              {correctOptionError}
            </span>
          </div>

          <div className="flex flex-row gap-5 self-end">
            <button
              onClick={handleCancel}
              type="button"
              className="border border-red-500 text-red-500 h-[32px] px-6 text-sm rounded-[4px]"
            >
              Clear
            </button>
            <button
              type="submit"
              className="text-white bg-adminRightTextPrimary h-[32px] px-6 text-sm rounded-[4px] flex flex-row items-center justify-center gap-2"
            >
              <FaPlus />
              <span>Add</span>
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default AddQuestionForm;
